import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Button as LumosButton, Icon, useNotify } from "@loanpal/lumos__core";
import {
  createPaymentCustomer,
  createPaymentInvoice,
  createPaymentTransaction,
  getPaymentsConfig,
  getToken as getTokenApi,
  resolveInvoiceLink,
  sendPaymentInvoice,
} from "../../utils/api";
import {
  BULK_CSV_HEADERS,
  BULK_CSV_SAMPLE_PATH,
  parseBulkInvoiceCsv,
  validateBulkInvoiceRows,
} from "../../utils/bulk-invoice-csv";
import { checkAndResolveSubmittingUserWithRetry } from "../../utils/submitting-user";
import { fetchPaymentsLocalActorOverride } from "../../utils/payments-local";
import { omitNamePlusSuffix } from "../../utils/format";

const PAY = {
  radiusSm: "10px",
  accent: "#472BA4",
  accentHover: "#200F51",
  accentSoft: "rgba(71, 43, 164, 0.1)",
};

const primaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  bg: PAY.accent,
  color: "white",
  border: "1px solid",
  borderColor: PAY.accent,
  "&:hover": {
    bg: PAY.accentHover,
    borderColor: PAY.accentHover,
    color: "white",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

const secondaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  color: PAY.accent,
  borderColor: PAY.accent,
  bg: "transparent",
  "&:hover": {
    bg: PAY.accentSoft,
    color: PAY.accentHover,
    borderColor: PAY.accentHover,
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

function actorFromSubmittingUser(user) {
  if (!user?.email) return undefined;
  return {
    email: String(user.email).trim(),
    firstName: omitNamePlusSuffix(user.firstName) || undefined,
    lastName: omitNamePlusSuffix(user.lastName) || undefined,
  };
}

function dollarsToCents(dollars) {
  return Math.round(Number(dollars) * 100);
}

const TABLE_COLS = [
  { key: "status", label: "", width: 36 },
  { key: "rowNumber", label: "#" },
  { key: "email", label: "Email" },
  { key: "fname", label: "First" },
  { key: "lname", label: "Last" },
  { key: "referenceId", label: "Ref" },
  { key: "amount", label: "Amount" },
  { key: "city", label: "City" },
  { key: "state", label: "ST" },
  { key: "detail", label: "Notes" },
  { key: "invoiceLink", label: "Invoice", width: 48 },
];

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildResultsCsv(rows) {
  const headers = [...BULK_CSV_HEADERS, "valid", "notes", "sendStatus", "invoiceLink"];
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    const notes =
      row.sendStatus === "failed"
        ? row.sendError
        : row.sendStatus === "sent"
          ? "Sent"
          : row.valid
            ? "Ready"
            : (row.errors || []).join("; ");
    lines.push(
      [
        row.email,
        row.fname,
        row.lname,
        row.street,
        row.address2,
        row.city,
        row.state,
        row.zip,
        row.referenceId,
        row.amount,
        row.valid ? "true" : "false",
        notes,
        row.sendStatus || "idle",
        row.invoiceUrl || "",
      ]
        .map(escapeCsvCell)
        .join(",")
    );
  });
  return lines.join("\n");
}

/**
 * Bulk CSV → create invoice + email send, one row at a time.
 * Requires SharedApp with clientId + submittingUser (or local actor override).
 */
export default function BulkInvoiceSend({ sharedApp }) {
  const { addSnack, addToast } = useNotify();
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [fileName, setFileName] = useState("");
  const [actor, setActor] = useState(undefined);
  const [actorLoading, setActorLoading] = useState(false);
  const [actorError, setActorError] = useState("");
  const [elevatedToken, setElevatedToken] = useState(undefined);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [apiEnvironment, setApiEnvironment] = useState("sandbox");

  const clientId = String(sharedApp?.clientId || "").trim();
  const submittingUserEmail = String(sharedApp?.submittingUser?.email || "").trim();
  const submittingUserFirstName = String(
    sharedApp?.submittingUser?.firstName || ""
  ).trim();
  const submittingUserLastName = String(
    sharedApp?.submittingUser?.lastName || ""
  ).trim();
  const useConnectedOrgs = Boolean(sharedApp?.useConnectedOrgs);
  const parentOrganizationClientId = String(
    sharedApp?.parentOrganizationClientId || ""
  ).trim();
  const organizationId = String(sharedApp?.organizationId || "").trim();
  const sharedAppId = String(sharedApp?.id || "").trim();

  const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
  const invalidCount = rows.length - validRows.length;
  const canSend =
    Boolean(actor) &&
    Boolean(clientId) &&
    validRows.length > 0 &&
    !sending &&
    !actorLoading;

  useEffect(() => {
    let cancelled = false;
    async function resolveActorAndToken() {
      if (!sharedAppId) {
        setActor(undefined);
        setElevatedToken(undefined);
        setActorError("");
        setActorLoading(false);
        return;
      }
      setActorLoading(true);
      setActorError("");
      setActor(undefined);
      setElevatedToken(undefined);

      try {
        let token;
        if (useConnectedOrgs && parentOrganizationClientId && organizationId) {
          const t = await getTokenApi(
            parentOrganizationClientId,
            organizationId
          );
          token = t?.token || undefined;
        }
        if (!cancelled) setElevatedToken(token);

        try {
          const cfg = await getPaymentsConfig(clientId, token);
          if (!cancelled && cfg?.apiEnvironment === "production") {
            setApiEnvironment("production");
          } else if (!cancelled) {
            setApiEnvironment("sandbox");
          }
        } catch {
          if (!cancelled) setApiEnvironment("sandbox");
        }

        const localActor = await fetchPaymentsLocalActorOverride();
        if (cancelled) return;
        if (localActor) {
          setActor(actorFromSubmittingUser(localActor));
          setActorLoading(false);
          return;
        }

        const configured = {
          email: submittingUserEmail,
          firstName: submittingUserFirstName,
          lastName: submittingUserLastName,
        };
        if (!clientId || !configured.email) {
          setActorError(
            "This GoodLink Payments ID has no submitting user — bulk send needs one."
          );
          setActorLoading(false);
          return;
        }

        const result = await checkAndResolveSubmittingUserWithRetry(
          clientId,
          configured.email,
          token
        );
        if (cancelled) return;

        let sourceUser = configured;
        if (result.status === "configured") {
          sourceUser = configured;
        } else if (result.status === "substituted" && result.substitute) {
          sourceUser = result.substitute;
        } else {
          setActorError(
            result.reason ||
              "Could not verify submitting user for payment actor."
          );
          setActor(undefined);
          return;
        }

        const next = actorFromSubmittingUser(sourceUser);
        if (!next) {
          setActorError("Could not resolve payment actor.");
        } else {
          setActor(next);
        }
      } catch (err) {
        if (!cancelled) {
          setActorError(err?.message || "Failed to resolve payment actor.");
        }
      } finally {
        if (!cancelled) setActorLoading(false);
      }
    }
    resolveActorAndToken();
    return () => {
      cancelled = true;
    };
  }, [
    sharedAppId,
    clientId,
    submittingUserEmail,
    submittingUserFirstName,
    submittingUserLastName,
    useConnectedOrgs,
    parentOrganizationClientId,
    organizationId,
  ]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParseError("");
    setFileName(file.name);
    setProgress({ done: 0, total: 0, current: "" });
    try {
      const text = await file.text();
      const parsed = parseBulkInvoiceCsv(text);
      if (parsed.error) {
        setRows([]);
        setParseError(parsed.error);
        addSnack({ variant: "danger", message: parsed.error });
        return;
      }
      const validated = validateBulkInvoiceRows(parsed.rows);
      setRows(validated);
      const ok = validated.filter((r) => r.valid).length;
      const bad = validated.length - ok;
      addToast({
        variant: bad ? "warning" : "success",
        message: `Loaded ${validated.length} row(s): ${ok} valid, ${bad} invalid.`,
      });
    } catch (err) {
      setRows([]);
      setParseError(err?.message || "Failed to read CSV");
      addSnack({
        variant: "danger",
        message: err?.message || "Failed to read CSV",
      });
    }
  };

  const sendOneRow = async (row) => {
    const customer = await createPaymentCustomer(
      clientId,
      {
        email: row.email,
        firstName: omitNamePlusSuffix(row.fname),
        lastName: omitNamePlusSuffix(row.lname),
        actor,
        address: {
          line1: row.street,
          line2: row.address2 || undefined,
          city: row.city,
          state: row.state,
          postalCode: row.zip,
        },
      },
      elevatedToken
    );
    if (!customer?.id) throw new Error("Failed to create customer");

    const transaction = await createPaymentTransaction(
      clientId,
      {
        amount: dollarsToCents(row.amountDollars),
        customerId: customer.id,
        description: `Invoice ${row.referenceId}`,
        referenceNumber: row.referenceId,
        actor,
      },
      elevatedToken
    );
    if (!transaction?.id) throw new Error("Failed to create transaction");

    const invoice = await createPaymentInvoice(
      clientId,
      { transactionId: transaction.id, actor },
      elevatedToken
    );
    if (!invoice?.id) throw new Error("Failed to create invoice");

    await sendPaymentInvoice(clientId, {
      invoiceId: invoice.id,
      method: "email",
      email: row.email,
    });

    return resolveInvoiceLink(invoice, apiEnvironment);
  };

  const handleExportResults = () => {
    if (!rows.length) return;
    const csv = buildResultsCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk-invoice-results-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    addToast({ variant: "success", message: "Results CSV downloaded." });
  };

  const handleClear = () => {
    if (sending) return;
    setRows([]);
    setParseError("");
    setFileName("");
    setProgress({ done: 0, total: 0, current: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSendAll = async () => {
    if (!canSend) return;
    const targets = rows.filter((r) => r.valid);
    setSending(true);
    setProgress({ done: 0, total: targets.length, current: "" });

    let sent = 0;
    let failed = 0;

    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i];
      setProgress({
        done: i,
        total: targets.length,
        current: target.email,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.rowNumber === target.rowNumber
            ? { ...r, sendStatus: "sending", sendError: "" }
            : r
        )
      );
      try {
        const invoiceUrl = await sendOneRow(target);
        sent += 1;
        setRows((prev) =>
          prev.map((r) =>
            r.rowNumber === target.rowNumber
              ? {
                  ...r,
                  sendStatus: "sent",
                  sendError: "",
                  invoiceUrl: invoiceUrl || "",
                }
              : r
          )
        );
      } catch (err) {
        failed += 1;
        const msg = err?.message || "Send failed";
        setRows((prev) =>
          prev.map((r) =>
            r.rowNumber === target.rowNumber
              ? { ...r, sendStatus: "failed", sendError: msg }
              : r
          )
        );
      }
      setProgress({
        done: i + 1,
        total: targets.length,
        current: target.email,
      });
    }

    setSending(false);
    addToast({
      variant: failed ? "warning" : "success",
      message: `Bulk send finished: ${sent} sent, ${failed} failed.`,
    });
  };

  const statusIcon = (row) => {
    if (row.sendStatus === "sent") {
      return <Icon variant="color-success" size="sm" />;
    }
    if (row.sendStatus === "failed") {
      return <Icon variant="color-danger" size="sm" />;
    }
    if (row.sendStatus === "sending") {
      return (
        <Box sx={{ fontSize: 0, color: "neutral.d2", fontWeight: 600 }}>…</Box>
      );
    }
    if (row.valid) {
      return <Icon variant="color-success" size="sm" />;
    }
    return <Icon variant="color-danger" size="sm" />;
  };

  const noteForRow = (row) => {
    if (row.sendStatus === "failed" && row.sendError) return row.sendError;
    if (row.sendStatus === "sent") return "Sent";
    if (row.sendStatus === "sending") return "Sending…";
    if (!row.valid) return row.errors.join("; ");
    return "Ready";
  };

  if (!sharedApp?.id) {
    return (
      <Box sx={{ color: "neutral.d2", fontSize: 1 }}>
        Enter a valid GoodLink Payments ID above to enable bulk invoice send.
      </Box>
    );
  }

  return (
    <Box>
      <HeadingLike>Bulk Invoice Send</HeadingLike>
      <Box sx={{ mb: 3, color: "neutral.d2", fontSize: 1, lineHeight: 1.5 }}>
        Upload a CSV file to send invoices by email in bulk.{" "}
        <Box
          as="a"
          href={BULK_CSV_SAMPLE_PATH}
          download="bulk-payments-sample.csv"
          sx={{ color: PAY.accent, fontWeight: 600 }}
        >
          Download a sample CSV file here
        </Box>
        . All fields are required except address2.
      </Box>

      {actorError ? (
        <Box sx={{ mb: 3, color: "danger.base", fontSize: 1 }}>{actorError}</Box>
      ) : null}
      {parseError ? (
        <Box sx={{ mb: 3, color: "danger.base", fontSize: 1 }}>{parseError}</Box>
      ) : null}

      <Flex sx={{ gap: 2, flexWrap: "wrap", mb: 3, alignItems: "center" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFileChange}
          style={{ display: "none" }}
        />
        <LumosButton
          type="button"
          variant="secondary"
          onClick={onPickFile}
          disabled={sending}
          sx={secondaryButtonSx}
        >
          Upload CSV file
        </LumosButton>
        {fileName ? (
          <Box sx={{ fontSize: 1, color: "neutral.d2" }}>{fileName}</Box>
        ) : null}
        {rows.length > 0 ? (
          <Box sx={{ fontSize: 1, color: "neutral.d2" }}>
            {validRows.length} valid · {invalidCount} invalid
          </Box>
        ) : null}
        {rows.length > 0 ? (
          <LumosButton
            type="button"
            variant="secondary"
            onClick={handleExportResults}
            disabled={sending}
            sx={secondaryButtonSx}
          >
            Export results CSV
          </LumosButton>
        ) : null}
      </Flex>

      {rows.length > 0 ? (
        <Box
          sx={{
            mb: 3,
            maxHeight: 20 * 40 + 40,
            overflow: "auto",
            border: "1px solid",
            borderColor: "neutral.l3",
            borderRadius: PAY.radiusSm,
            bg: "white",
          }}
        >
          <Box
            as="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 1,
              minWidth: 720,
            }}
          >
            <Box as="thead" sx={{ position: "sticky", top: 0, bg: "neutral.l4" }}>
              <Box as="tr">
                {TABLE_COLS.map((col) => (
                  <Box
                    as="th"
                    key={col.key}
                    sx={{
                      textAlign: "left",
                      px: 2,
                      py: 2,
                      fontWeight: 600,
                      borderBottom: "1px solid",
                      borderColor: "neutral.l3",
                      whiteSpace: "nowrap",
                      width: col.width,
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {rows.map((row) => (
                <Box
                  as="tr"
                  key={row.rowNumber}
                  sx={{
                    bg: row.valid ? "transparent" : "rgba(186, 24, 27, 0.04)",
                    "&:hover": { bg: "neutral.l5" },
                  }}
                >
                  <Box as="td" sx={{ px: 2, py: 2, verticalAlign: "middle" }}>
                    {statusIcon(row)}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.rowNumber}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.email}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.fname}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.lname}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.referenceId}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.amount}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.city}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2 }}>
                    {row.state}
                  </Box>
                  <Box
                    as="td"
                    sx={{
                      px: 2,
                      py: 2,
                      color: row.valid ? "neutral.d2" : "danger.base",
                      maxWidth: 220,
                    }}
                  >
                    {noteForRow(row)}
                  </Box>
                  <Box as="td" sx={{ px: 2, py: 2, verticalAlign: "middle" }}>
                    {row.invoiceUrl ? (
                      <Box
                        as="a"
                        href={row.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open invoice"
                        aria-label="Open invoice link"
                        sx={{
                          display: "inline-flex",
                          color: PAY.accent,
                          "&:hover": { color: PAY.accentHover },
                        }}
                      >
                        <Icon variant="open-new-tab" size="sm" />
                      </Box>
                    ) : (
                      <Box as="span" sx={{ color: "neutral.l2" }}>
                        —
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}

      {sending ? (
        <Box sx={{ mb: 3, fontSize: 1, color: PAY.accentHover }}>
          Sending {progress.done} of {progress.total}
          {progress.current ? ` — ${progress.current}` : ""}…
          <Box
            sx={{
              mt: 2,
              height: 8,
              borderRadius: 4,
              bg: "neutral.l3",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${
                  progress.total
                    ? Math.round((progress.done / progress.total) * 100)
                    : 0
                }%`,
                bg: PAY.accent,
                transition: "width 0.2s ease",
              }}
            />
          </Box>
        </Box>
      ) : null}

      <Flex
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <LumosButton
          type="button"
          onClick={handleSendAll}
          disabled={!canSend}
          sx={{
            ...primaryButtonSx,
            opacity: canSend ? 1 : 0.45,
            boxShadow: canSend ? `0 0 0 3px ${PAY.accentSoft}` : "none",
          }}
        >
          {sending
            ? `Sending… ${progress.done}/${progress.total}`
            : actorLoading
              ? "Preparing…"
              : "Create invoices and send"}
        </LumosButton>
        <LumosButton
          type="button"
          variant="secondary"
          onClick={handleClear}
          disabled={sending || (!rows.length && !fileName && !parseError)}
          sx={secondaryButtonSx}
        >
          Clear
        </LumosButton>
      </Flex>
    </Box>
  );
}

function HeadingLike({ children }) {
  return (
    <Box
      sx={{
        fontWeight: 600,
        fontSize: 3,
        color: PAY.accentHover,
        mb: 2,
      }}
    >
      {children}
    </Box>
  );
}
